import React from 'react';
import { Box, Typography, Card, CardContent, Chip, Tooltip } from '@mui/material';
import { MessageOutlined, PersonOutlineOutlined, ScheduleOutlined } from '@mui/icons-material';
import { QuestionReportData } from '../types';
import { formatDistanceToNow } from 'date-fns';

interface CommentsSectionProps {
  questionReport: QuestionReportData;
  maxComments?: number;
  showEmptyState?: boolean;
  compact?: boolean;
  className?: string;
}

interface CommentItemProps {
  comment: {
    responseId: string;
    comment: string;
    timestamp: string;
    language?: string;
    colorCode?: string;
    answerValue?: string | number | boolean | string[];
  };
  compact?: boolean;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, compact = false }) => {
  const formatTimestamp = (timestamp: string): string => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return 'Unknown time';
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: compact ? 1 : 2,
        backgroundColor: 'transparent',
        border: '2px solid',
        borderColor: comment.colorCode || 'divider',
        boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.012), 0px 2px 8px rgba(0, 0, 0, 0.012)',
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        {/* Comment text and header on same line */}
        <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
          <Typography
            variant="body2"
            sx={{
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              flex: 1,
            }}
          >
            {comment.comment}
          </Typography>

          <Box display="flex" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
            <Tooltip title={new Date(comment.timestamp).toLocaleString()}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <ScheduleOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {formatTimestamp(comment.timestamp)}
                </Typography>
              </Box>
            </Tooltip>

            <Chip
              label={comment.responseId}
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: 'transparent',
                borderColor: comment.colorCode || 'primary.main',
                color: comment.colorCode || 'primary.main',
                fontWeight: 'bold',
              }}
              icon={<PersonOutlineOutlined sx={{ fontSize: 16 }} />}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const CommentsSection: React.FC<CommentsSectionProps> = ({
  questionReport,
  maxComments,
  showEmptyState = true,
  compact = false,
  className,
}) => {
  const { includedComments } = questionReport;

  // Determine which comments to show
  const commentsToShow = maxComments ? includedComments.slice(0, maxComments) : includedComments;

  if (includedComments.length === 0) {
    if (!showEmptyState) return null;

    return (
      <Box className={className} textAlign="center" py={4}>
        <MessageOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          No comments available for this question.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={className}>
      {/* Section header */}
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <MessageOutlined sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography variant="h6" component="h3">
          Comments ({includedComments.length})
        </Typography>
      </Box>

      {/* Comments list */}
      <Box>
        {commentsToShow.map((comment, index) => (
          <CommentItem key={`${comment.responseId}-${index}`} comment={comment} compact={compact} />
        ))}

        {/* Show more indicator */}
        {maxComments && includedComments.length > maxComments && (
          <Box textAlign="center" mt={2}>
            <Typography variant="body2" color="text.secondary">
              ... and {includedComments.length - maxComments} more comments
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
